import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { Plus as PlusIcon, ArrowLeft } from '@creditornot/cb-icons';
import { Spinner, cssThemedValue } from '@creditornot/cb-ingredients';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { addVenuesToVenueGroup, fetchDetailVenueGroup, removeVenueFromVenueGroup } from 'apis/venue-groups/api';
import { Typography as T } from 'common/typography';
import { useBrandContext } from 'modules/brand/context/BrandContext';
import VenueAvailabilityStatus from 'modules/venue-availability-status';
import { VenueRow } from 'modules/venue-availability-status/types';

import {
  AddVenuesToVenueGroupModal,
  FormValues as AddVenuesToVenueGroupFormValues,
} from './AddVenuesToVenueGroupModal';
import { RemoveVenueFromVenueGroupModal } from './RemoveVenueFromVenueGroupModal';
import { onError } from './utils/onError';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  padding: 24px;
`;

const Heading = styled(T.Heading5)`
  margin-block-start: 16px;
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const Loading = () => (
  <LoadingSpinnerContainer>
    <Spinner size="large" />
  </LoadingSpinnerContainer>
);

export const DetailVenueGroup: FunctionComponent = () => {
  const { venueGroupId } = useParams<{ venueGroupId: string }>();
  const [isOpenRemoveVenueModal, setIsRemoveModalOpen] = useState(false);
  const [isOpenAddVenuesModal, setIsOpenAddVenuesModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<VenueRow>();
  const history = useHistory();
  const { brand } = useBrandContext();

  const openRemoveModal = (venue: VenueRow) => {
    setIsRemoveModalOpen(true);
    setSelectedVenue(venue);
  };

  const closeRemoveVenueModal = () => {
    setIsRemoveModalOpen(false);
    setSelectedVenue(undefined);
  };

  const {
    data: venueGroup,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['fetchDetailVenueGroup', venueGroupId],
    queryFn: () => fetchDetailVenueGroup(venueGroupId),
  });

  const mutationAddVenuesToVenueGroup = useMutation(
    async (values: AddVenuesToVenueGroupFormValues) => {
      const { venueIds } = values;
      await addVenuesToVenueGroup(venueGroupId, venueIds);
    },
    {
      onSuccess: () => {
        refetch();
        setIsOpenAddVenuesModal(false);
      },
      onError,
    },
  );

  const mutationRemoveVenueFromVenueGroup = useMutation(
    async (venueId: string) => {
      return await removeVenueFromVenueGroup(venueGroupId, venueId);
    },
    {
      onSuccess: () => {
        refetch();
        closeRemoveVenueModal();
      },
      onError,
    },
  );

  const backToVenueGroup = () => history.push(`/enterprise/${brand?.enterpriseId}/brand/${brand?.id}/venues/groups`);

  if (error) {
    return (
      <ErrorAlert>
        <FormattedMessage id="venue-groups.error" />
      </ErrorAlert>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  const header = () => (
    <Toolbar>
      <div data-testid="venue-group-navigation-heading">
        <TextButton onClick={backToVenueGroup} variant="grey" icon={<ArrowLeft />}>
          <T.Body3 fontWeight="bold">
            <FormattedMessage id="venue-groups.venue-groups" />
          </T.Body3>
        </TextButton>
        <Heading data-testid="venue-group-name">{venueGroup?.venueGroup.name}</Heading>
      </div>
      <Button variant="blue" size="small" onClick={() => setIsOpenAddVenuesModal(true)} icon={<PlusIcon />}>
        <FormattedMessage id="venue-groups.add-venue" />
      </Button>
    </Toolbar>
  );

  const handleRemoveVenueFromVenueGroup = async () => {
    if (!selectedVenue) {
      return;
    }
    await mutationRemoveVenueFromVenueGroup.mutateAsync(selectedVenue.id);
  };

  const handleAddVenuesToVenueGroup = async (values: AddVenuesToVenueGroupFormValues) => {
    await mutationAddVenuesToVenueGroup.mutateAsync(values);
  };

  return (
    <>
      {venueGroup && (
        <VenueAvailabilityStatus
          handleRemoveAction={openRemoveModal}
          renderHeader={header}
          entity={{ type: 'venue-group', venueGroup }}
        />
      )}

      <AddVenuesToVenueGroupModal
        isOpen={isOpenAddVenuesModal}
        onSubmit={handleAddVenuesToVenueGroup}
        onClose={() => setIsOpenAddVenuesModal(false)}
        initialVenueIds={venueGroup?.venueIds}
      />

      <RemoveVenueFromVenueGroupModal
        venueGroupId={venueGroupId}
        venue={selectedVenue}
        isOpen={isOpenRemoveVenueModal}
        onSubmit={handleRemoveVenueFromVenueGroup}
        onClose={closeRemoveVenueModal}
      />
    </>
  );
};
